
// Content for the armpit cysts article
export const armpitCystsContent = `
  <h2>What Are Armpit Cysts?</h2>
  <p>
    Armpit cysts are enclosed sacs that develop under the skin in the underarm area. They contain fluid, pus, or other materials and can vary in size from barely noticeable to several centimeters in diameter. While most armpit cysts are benign (non-cancerous), they can sometimes be uncomfortable, painful, or become infected.
  </p>
  
  <div class="my-10">
    <img src="/lovable-uploads/650a9c42-cc64-48c3-bca7-c4074d812e1a.png" alt="Understanding Armpit Cysts Infographic" class="rounded-lg mx-auto shadow-lg w-full md:w-4/5" />
    <p class="text-center text-sm text-muted-foreground mt-3">Infographic: Understanding armpit cysts - causes, symptoms, and treatment options</p>
  </div>
  
  <h2>Common Types of Armpit Cysts</h2>
  <p>
    Several different types of cysts can develop in the armpit region:
  </p>
  
  <h3>Epidermoid Cysts</h3>
  <p>
    These slow-growing cysts form when surface skin cells move deeper into the skin instead of shedding naturally. They're filled with keratin (a protein found in skin cells) and appear as small, round lumps that may have a small dark "plug" visible on the surface.
  </p>
  
  <h3>Sebaceous Cysts</h3>
  <p>
    Sebaceous cysts develop from blocked sebaceous glands, which normally produce sebum to lubricate the skin and hair. These cysts are filled with a thick, cheese-like substance and can sometimes have an unpleasant odor if they rupture.
  </p>
  
  <h3>Hidradenitis Suppurativa</h3>
  <p>
    This chronic inflammatory skin condition causes painful, boil-like lumps to form in areas with sweat glands, including the armpits. These lesions can become infected, rupture, and lead to scarring and the formation of sinus tracts (abnormal passages) between cysts.
  </p>
  
  <h3>Enlarged Lymph Nodes</h3>
  <p>
    While not true cysts, swollen lymph nodes in the armpit can sometimes be mistaken for cysts. The armpit contains approximately 20-40 lymph nodes, which can enlarge in response to infection, inflammation, or rarely, cancer.
  </p>
  
  <h2>Causes of Armpit Cysts</h2>
  
  <p>
    Armpit cysts can develop due to various factors:
  </p>
  
  <ul>
    <li><strong>Blocked hair follicles</strong> - Often caused by shaving, deodorant use, or tight clothing</li>
    <li><strong>Blocked sweat glands</strong> - The armpit has numerous sweat glands that can become clogged</li>
    <li><strong>Infections</strong> - Bacterial or fungal infections can lead to cyst formation</li>
    <li><strong>Genetic factors</strong> - Some types of cysts, like those in hidradenitis suppurativa, have a genetic component</li>
    <li><strong>Hormonal changes</strong> - Fluctuations in hormone levels can trigger cyst development</li>
    <li><strong>Trauma or injury</strong> - Damage to the skin or underlying tissue can result in cyst formation</li>
  </ul>
  
  <h2>Symptoms and Signs</h2>
  
  <p>
    The symptoms of armpit cysts can vary depending on the type, size, and whether they're infected:
  </p>
  
  <ul>
    <li><strong>Visible lump</strong> - A noticeable bump under the skin, which may be flesh-colored, white, or red</li>
    <li><strong>Pain or tenderness</strong> - Particularly if the cyst is inflamed or infected</li>
    <li><strong>Redness and warmth</strong> - Signs of inflammation around the cyst</li>
    <li><strong>Swelling</strong> - The area around the cyst may become puffy</li>
    <li><strong>Discharge</strong> - If the cyst ruptures, it may release fluid or pus</li>
    <li><strong>Odor</strong> - Some infected cysts produce an unpleasant smell</li>
    <li><strong>Mobility</strong> - Many cysts can be moved slightly under the skin with gentle pressure</li>
  </ul>
  
  <h2>When to See a Doctor</h2>
  
  <p>
    While many armpit cysts are harmless and may resolve on their own, certain situations warrant medical attention:
  </p>
  
  <ul>
    <li>A cyst that grows rapidly or is larger than 1 inch (2.5 cm) in diameter</li>
    <li>Severe pain or discomfort that interferes with arm movement</li>
    <li>Signs of infection, such as increased redness, warmth, severe pain, or fever</li>
    <li>A cyst that ruptures, causing significant pain or bleeding</li>
    <li>Recurring cysts in the same location</li>
    <li>Multiple cysts developing in the armpit or other body areas</li>
    <li>Any lump that persists for more than two weeks without improvement</li>
  </ul>
  
  <p>
    It's particularly important to consult a healthcare provider if you have no obvious explanation for an armpit lump, especially if you also experience unexplained weight loss, night sweats, or fatigue.
  </p>
  
  <h2>Diagnosis</h2>
  
  <p>
    To diagnose an armpit cyst, a healthcare provider will typically:
  </p>
  
  <ul>
    <li><strong>Physical examination</strong> - Inspecting and palpating the lump to assess its characteristics</li>
    <li><strong>Medical history</strong> - Asking about symptoms, duration, any changes in the lump, and risk factors</li>
    <li><strong>Ultrasound</strong> - Sometimes used to determine if the lump is solid or fluid-filled</li>
    <li><strong>Fine needle aspiration</strong> - Removing fluid or cells from the cyst for laboratory analysis</li>
    <li><strong>Biopsy</strong> - Taking a small tissue sample if there's concern about cancerous changes</li>
    <li><strong>Culture</strong> - Testing fluid from the cyst to identify any bacterial infection</li>
  </ul>
  
  <h2>Treatment Options</h2>
  
  <h3>Medical Treatments</h3>
  <p>
    Depending on the type, size, and symptoms of the armpit cyst, medical treatments may include:
  </p>
  
  <ul>
    <li><strong>Incision and drainage</strong> - Making a small cut to drain the contents of the cyst</li>
    <li><strong>Antibiotics</strong> - Prescribed if the cyst is infected or to prevent infection after drainage</li>
    <li><strong>Corticosteroid injection</strong> - Reducing inflammation in and around the cyst</li>
    <li><strong>Complete surgical removal</strong> - Extracting the entire cyst including its wall to prevent recurrence</li>
    <li><strong>Laser therapy</strong> - Sometimes used for certain types of recurring cysts</li>
    <li><strong>Medications for underlying conditions</strong> - Such as treatments for hidradenitis suppurativa</li>
  </ul>
  
  <h3>Home Care and Remedies</h3>
  <p>
    For minor, non-infected cysts, some home care approaches may provide relief:
  </p>
  
  <ul>
    <li><strong>Warm compresses</strong> - Applying a warm, damp cloth to the area several times daily to reduce pain and promote drainage</li>
    <li><strong>Over-the-counter pain relievers</strong> - Such as ibuprofen or acetaminophen to manage discomfort</li>
    <li><strong>Gentle cleansing</strong> - Keeping the area clean with mild, fragrance-free soap</li>
    <li><strong>Avoiding irritants</strong> - Temporarily discontinuing deodorants, antiperspirants, or other products that might irritate the area</li>
    <li><strong>Loose clothing</strong> - Wearing non-restrictive garments to reduce friction and irritation</li>
  </ul>
  
  <div class="warning-box">
    <p><strong>Important:</strong> Never attempt to squeeze, pop, or drain a cyst at home. This can lead to infection, scarring, and pushing the infection deeper into the skin.</p>
  </div>
  
  <h2>Prevention</h2>
  
  <p>
    While not all armpit cysts can be prevented, these measures may help reduce your risk:
  </p>
  
  <ul>
    <li><strong>Proper hygiene</strong> - Regular, gentle cleansing of the armpit area</li>
    <li><strong>Gentle shaving techniques</strong> - Using sharp, clean razors and shaving cream to minimize irritation</li>
    <li><strong>Consider hair removal alternatives</strong> - Methods like trimming may cause less irritation than shaving</li>
    <li><strong>Wear breathable fabrics</strong> - Natural fibers like cotton allow better air circulation</li>
    <li><strong>Use non-comedogenic products</strong> - Deodorants and antiperspirants that won't clog pores</li>
    <li><strong>Maintain a healthy weight</strong> - Excess weight can increase friction in the armpit area</li>
    <li><strong>Manage underlying conditions</strong> - Addressing conditions like hidradenitis suppurativa with appropriate treatment</li>
  </ul>
  
  <h2>Long-Term Outlook</h2>
  
  <p>
    Most armpit cysts are benign and can be effectively treated. However, some people may experience recurring cysts, especially those with conditions like hidradenitis suppurativa. Working with a healthcare provider to develop a comprehensive management plan is essential for those with chronic or recurring cysts.
  </p>
  
  <p>
    Remember that early intervention typically leads to better outcomes. If you're concerned about an armpit cyst, consulting with a healthcare provider is the best approach to proper diagnosis and treatment.
  </p>
`;
